import { useMemo, useRef, useEffect } from "react"
import { type CustomTooltipProps } from "ag-grid-react"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { formatNumber } from "~/utils/formatting"
import HeaderTooltip from "./HeaderTooltip"
import { MiniMatrix } from "~/components/reusable/miniTable/MiniMatrix"
import { api } from "~/utils/api"
import Highcharts from "highcharts"
import { HighchartsReact } from "highcharts-react-official"
import { useCompany } from "~/hooks/useCompany"
import { useBidirectionalHover } from "~/hooks/useBidirectionalHover"
import { generateDistinctColors } from "~/utils/colors"
import { sortBy } from "lodash"

const PRICE_COMPONENTS = [
  {
    price: "variantEmpiricalPrice",
    weight: "weightVariantEmpirical",
    key: "variantEmpirical",
    name: "What this variant sold for (same channel)",
  },
  {
    price: "variantNormalizedPrice",
    weight: "weightVariantNormalized",
    key: "variantNormalized",
    name: "What this variant sold for (all channels)",
  },
  {
    price: "productEmpiricalPrice",
    weight: "weightProductEmpirical",
    key: "productEmpirical",
    name: "What this product sold for (same channel)",
  },
  {
    price: "productNormalizedPrice",
    weight: "weightProductNormalized",
    key: "productNormalized",
    name: "What this product sold for (all channels)",
  },
  {
    price: "nnEmpiricalPrice",
    weight: "weightNn",
    key: "nnEmpirical",
    name: "Similar products' prices",
  },
  {
    price: "markUpBased",
    weight: "weightMarkUp",
    key: "markUpBased",
    name: "Cost + markup calculation",
  },
  {
    price: "categoryAverage",
    weight: "weightCategory",
    key: "categoryAverage",
    name: "Category average price",
  },
  {
    price: "globalAverage",
    weight: "weightGlobal",
    key: "globalAverage",
    name: "Overall average price",
  },
] as const

const PRICE_COMPONENT_COLORS = generateDistinctColors(PRICE_COMPONENTS.length)

type PriceComponentData = Array<{
  variantId: string
  warehouseId: string
  channel: string
  estimatedSalesPrice: number
  variantEmpiricalPrice: number | null
  variantNormalizedPrice: number | null
  productEmpiricalPrice: number | null
  productNormalizedPrice: number | null
  nnEmpiricalPrice: number | null
  markUpBased: number | null
  categoryChannelWarehouseAverage: number | null
  categoryAverage: number | null
  globalAverage: number | null
  weightVariantEmpirical: number | null
  weightVariantNormalized: number | null
  weightProductEmpirical: number | null
  weightProductNormalized: number | null
  weightNn: number | null
  weightMarkUp: number | null
  weightCategory: number | null
  weightGlobal: number | null
}>

interface PriceComponentChartProps {
  data: PriceComponentData
  currency: string
  avgPrice: number
  isAggregated?: boolean
  variantCount?: number
  hoveredLocation?: { warehouseId: string; channel: string } | null
  onBarHover?: (warehouseId: string, channel: string) => void
  onBarLeave?: () => void
}

const PriceComponentChart = ({
  data,
  currency,
  avgPrice,
  isAggregated,
  variantCount,
  hoveredLocation,
  onBarHover,
  onBarLeave,
}: PriceComponentChartProps) => {
  const hasAnimated = useRef(false)

  const chartOptions = useMemo(() => {
    const categories = data.map((d) => `${d.channel} / ${d.warehouseId}`)
    categories.push("━━━ AVERAGE ━━━")

    // calculate total weight for each row to normalize
    const totalWeights = data.map((row) => {
      return PRICE_COMPONENTS.reduce((sum, { weight }) => {
        const weightVal = row[weight as keyof typeof row] as number | null
        return sum + (weightVal || 0)
      }, 0)
    })

    // calculate average weight for each component across all locations
    const avgWeights: Record<string, number> = {}
    PRICE_COMPONENTS.forEach(({ weight, key }) => {
      const weights: number[] = []
      data.forEach((row, idx) => {
        const weightVal = row[weight as keyof typeof row] as number | null
        const totalWeight = totalWeights[idx]
        if (totalWeight && totalWeight > 0 && weightVal) {
          weights.push(weightVal / totalWeight)
        }
      })
      avgWeights[key] =
        weights.length > 0 ? weights.reduce((sum, v) => sum + v, 0) / weights.length : 0
    })

    const totalAvgWeight = Object.values(avgWeights).reduce((sum, w) => sum + w, 0)

    // calculate average contributions using the average price
    const avgContributions: Record<string, number> = {}
    PRICE_COMPONENTS.forEach(({ key }) => {
      const weight = avgWeights[key]
      avgContributions[key] =
        totalAvgWeight > 0 && weight !== undefined ? (weight / totalAvgWeight) * avgPrice : 0
    })

    const series = PRICE_COMPONENTS.map(({ weight, key, name }, componentIndex) => {
      const seriesData = data.map((row, idx) => {
        const weightVal = row[weight as keyof typeof row] as number | null
        const totalWeight = totalWeights[idx]
        const value =
          !weightVal || weightVal === 0 || !totalWeight || totalWeight === 0
            ? 0
            : (weightVal / totalWeight) * row.estimatedSalesPrice

        const isHovered = hoveredLocation
          ? row.warehouseId === hoveredLocation.warehouseId &&
            row.channel === hoveredLocation.channel
          : false

        return {
          y: value,
          warehouseId: row.warehouseId,
          channel: row.channel,
          opacity: hoveredLocation ? (isHovered ? 1 : 0.3) : 1,
        }
      })

      // Add average bar (last bar)
      seriesData.push({
        y: avgContributions[key] ?? 0,
        warehouseId: "AVERAGE",
        channel: "AVERAGE",
        opacity: hoveredLocation ? 0.3 : 1,
      })

      if (!seriesData.some((point) => point.y > 0)) return null

      return {
        type: "bar" as const,
        name,
        data: seriesData,
        color: PRICE_COMPONENT_COLORS[componentIndex],
        stacking: "normal" as const,
        cursor: "pointer",
      }
    }).filter((s) => s !== null)

    const titleText = isAggregated
      ? `Price Component Breakdown - ${variantCount} variants (Avg: ${currency} ${avgPrice.toFixed(2)})`
      : `Price Component Breakdown (Avg: ${currency} ${avgPrice.toFixed(2)})`

    return {
      chart: { height: 200, backgroundColor: "transparent", animation: false },
      title: {
        text: titleText,
        style: { fontSize: "14px", fontWeight: "600", color: "var(--color-fg-base)" },
      },
      xAxis: {
        categories,
        labels: { style: { fontSize: "11px", color: "var(--color-fg-base)" } },
      },
      yAxis: {
        title: {
          text: `Price (${currency})`,
          style: { fontSize: "11px", color: "var(--color-fg-base)" },
        },
        labels: { style: { fontSize: "10px", color: "var(--color-fg-base)" } },
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          animation: !hasAnimated.current,
        },
        bar: {
          stacking: "normal",
          dataLabels: { enabled: false },
          borderWidth: 0,
          point: {
            events: {
              mouseOver: function (this: any) {
                if (this.warehouseId && this.channel && this.warehouseId !== "AVERAGE") {
                  onBarHover?.(this.warehouseId, this.channel)
                }
              },
              mouseOut: function () {
                onBarLeave?.()
              },
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        shared: false,
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        borderRadius: 8,
        padding: 6,
        shadow: {
          color: "rgba(0, 0, 0, 0.1)",
          offsetX: 0,
          offsetY: 2,
          opacity: 0.15,
          width: 4,
        },
        style: {
          fontSize: "12px",
          color: "#1f2937",
          fontWeight: "400",
        },
        useHTML: true,
        formatter: function (this: any): string {
          const pct = this.percentage ? this.percentage.toFixed(1) : 0
          return `
            <div style="padding: 6px; min-width: 140px;">
              <div style="font-weight: 600; margin-bottom: 4px; color: #111827; font-size: 12px; line-height: 1.2;">
                ${this.series.name}
              </div>
              <div style="margin-bottom: 3px; font-size: 12px; color: #374151;">
                Contribution: ${currency} ${this.y.toFixed(2)} (weight: ${pct}%)
              </div>
              <div style="border-top: 1px solid #e5e7eb; margin-top: 4px; padding-top: 3px; font-size: 12px; color: #374151;">
                Total: ${currency} ${this.total.toFixed(2)}
              </div>
            </div>
          `
        },
      },
      credits: { enabled: false },
      series,
    } as Highcharts.Options
  }, [
    data,
    currency,
    avgPrice,
    isAggregated,
    variantCount,
    hoveredLocation,
    onBarHover,
    onBarLeave,
  ])

  useEffect(() => {
    hasAnimated.current = true
  }, [])

  return (
    <div className="border-t border-base pt-3">
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  )
}

interface SalesPriceMatrixTooltipProps extends CustomTooltipProps {
  currency?: string
  value?: {
    variantId?: string
    variantIds?: string[]
    isAggregated?: boolean
    avgPrice?: number
    prices: Array<{
      warehouseId: string
      warehouseName: string
      channel: string
      channelName: string
      price: number
    }>
    priceComponents?: Array<{
      warehouseId: string
      channel: string
      components: {
        variantEmpirical?: number
        variantNormalized?: number
        productEmpirical?: number
        productNormalized?: number
        nearestNeighbor?: number
        costMarkup?: number
        category?: number
        global?: number
      }
    }>
  }
}

const SalesPriceMatrixTooltip = (props: SalesPriceMatrixTooltipProps) => {
  const { data: company } = useCompany()
  const {
    prices = [],
    isAggregated = false,
    variantIds = [],
    variantId,
    avgPrice,
  } = props.value || {}

  // Use custom hook for bidirectional hover state management (must be before early returns)
  const {
    hoveredLocation,
    handleMatrixCellHover,
    handleMatrixCellLeave,
    handleBarHover,
    handleBarLeave,
    getHoveredCell,
  } = useBidirectionalHover()

  // fetch component data for single variant
  const { data: singleVariantData } = api.priceEstimation.getVariantPriceData.useQuery(
    { variantId: variantId ?? "" },
    { enabled: !!variantId && !isAggregated },
  )

  // fetch aggregated component data for multiple variants
  const { data: aggregatedVariantData } = api.priceEstimation.getAggregatedPriceData.useQuery(
    { variantIds: variantIds },
    { enabled: isAggregated && variantIds.length > 0 },
  )

  const componentData = isAggregated ? aggregatedVariantData : singleVariantData

  // only show component breakdown if we have data and the average price is greater than 0
  const showComponentBreakdown =
    !!componentData && componentData.length > 0 && !!avgPrice && avgPrice > 0

  if (props.location === "header") return <HeaderTooltip {...props} />
  if (prices.length === 0) return null

  const onMouseLeave = () => props.hideTooltipCallback?.()

  const priceData = prices
  const currency = props.currency || company?.currency || "$"
  const variantCount = variantIds.length

  const warehouses = Array.from(
    new Map(
      priceData.map((p) => [p.warehouseId, { id: p.warehouseId, name: p.warehouseName }]),
    ).values(),
  )

  const channels = Array.from(
    new Map(priceData.map((p) => [p.channel, { id: p.channel, name: p.channelName }])).values(),
  )

  const columnHeaders = warehouses.map((wh) => wh.name)
  const rowHeaders = channels.map((ch) => ch.name)

  const data = channels.map((channel) => {
    const row: Array<string | number | undefined> = []

    warehouses.forEach((warehouse) => {
      const priceEntry = priceData.find(
        (p) => p.warehouseId === warehouse.id && p.channel === channel.id,
      )
      row.push(priceEntry ? `${currency} ${formatNumber(priceEntry.price, 2)}` : undefined)
    })

    return row
  })

  const hoveredCell = getHoveredCell(channels, warehouses)

  return (
    <div className="bg-base w-[500px] mr-6 z-10" onMouseLeave={onMouseLeave}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            {isAggregated
              ? `Average Price by Warehouse & Channel (${variantCount} variants)`
              : "Price by Warehouse & Channel"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MiniMatrix
            columnHeaders={columnHeaders}
            rowHeaders={rowHeaders}
            data={data}
            centerText
            emptyText="No price"
            onCellHover={(rowIndex, colIndex) =>
              handleMatrixCellHover(rowIndex, colIndex, channels, warehouses)
            }
            onCellLeave={handleMatrixCellLeave}
            hoveredCell={hoveredCell}
          />
          {showComponentBreakdown && componentData && (
            <PriceComponentChart
              data={sortBy(componentData, [
                (d) => channels.findIndex((c) => c.id === d.channel),
                (d) => warehouses.findIndex((w) => w.id === d.warehouseId),
              ])}
              currency={currency}
              avgPrice={avgPrice ?? 0}
              isAggregated={isAggregated}
              variantCount={variantIds.length}
              hoveredLocation={hoveredLocation}
              onBarHover={handleBarHover}
              onBarLeave={handleBarLeave}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default SalesPriceMatrixTooltip
